import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import SmsService from '../../../services/smsService';

const MessageModal = ({ isOpen, onClose, onSendMessage, selectedAttendees, allAttendees }) => {
  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    messageType: 'general',
    sendTo: 'selected',
    deliveryMethod: 'email' // 'email', 'sms', 'both'
  });
  const [isLoading, setIsLoading] = useState(false);

  const messageTemplates = [
    { value: '', label: 'Select a template...' },
    { value: 'welcome', label: 'Welcome Message' },
    { value: 'reminder', label: 'Event Reminder' },
    { value: 'checkin', label: 'Check-in Instructions' },
    { value: 'update', label: 'Event Update' },
    { value: 'thankyou', label: 'Thank You Message' }
  ];

  const messageTypeOptions = [
    { value: 'general', label: 'General Information' },
    { value: 'urgent', label: 'Urgent Notice' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'update', label: 'Event Update' }
  ];

  const sendToOptions = [
    { value: 'selected', label: `Selected Attendees (${selectedAttendees?.length})` },
    { value: 'all', label: `All Attendees (${allAttendees?.length})` },
    { value: 'checkedin', label: 'Checked-in Attendees Only' },
    { value: 'notcheckedin', label: 'Not Checked-in Attendees Only' }
  ];

  const deliveryMethodOptions = [
    { value: 'email', label: 'Email Only' },
    { value: 'sms', label: 'SMS Only' },
    { value: 'both', label: 'Email & SMS' }
  ];

  const templates = {
    welcome: {
      subject: 'Welcome to [Event Name]!',
      message: `Dear [Attendee Name],\n\nWelcome to [Event Name]! We're excited to have you join us.\n\nEvent Details:\n• Date: [Event Date]\n• Time: [Event Time]\n• Location: [Event Location]\n\nPlease arrive 30 minutes early for check-in. Don't forget to bring your ticket (digital or printed).\n\nWe look forward to seeing you there!\n\nBest regards,\nThe Event Team`
    },
    reminder: {
      subject: 'Reminder: [Event Name] is Tomorrow!',
      message: `Dear [Attendee Name],\n\nThis is a friendly reminder that [Event Name] is happening tomorrow!\n\nEvent Details:\n• Date: [Event Date]\n• Time: [Event Time]\n• Location: [Event Location]\n\nRemember to:\n• Arrive 30 minutes early\n• Bring your ticket\n• Check parking information\n\nWe can't wait to see you there!\n\nBest regards,\nThe Event Team`
    },
    checkin: {
      subject: 'Check-in Instructions for [Event Name]',
      message: `Dear [Attendee Name],\n\nHere are your check-in instructions for [Event Name]:\n\n1. Arrive at the venue 30 minutes before the event starts\n2. Look for the registration desk at the main entrance\n3. Have your ticket ready (QR code on your phone or printed ticket)\n4. Our staff will scan your ticket and provide your name badge\n\nIf you have any issues, please don't hesitate to ask our staff for assistance.\n\nSee you soon!\n\nBest regards,\nThe Event Team`
    },
    update: {
      subject: 'Important Update: [Event Name]',
      message: `Dear [Attendee Name],\n\nWe have an important update regarding [Event Name]:\n\n[Update Details]\n\nAll other event details remain the same:\n• Date: [Event Date]\n• Time: [Event Time]\n• Location: [Event Location]\n\nIf you have any questions, please contact us at [Contact Information].\n\nThank you for your understanding.\n\nBest regards,\nThe Event Team`
    },
    thankyou: {
      subject: 'Thank You for Attending [Event Name]!',
      message: `Dear [Attendee Name],\n\nThank you for attending [Event Name]! We hope you had a wonderful experience.\n\nWe'd love to hear your feedback:\n• How was your overall experience?\n• What did you enjoy most?\n• Any suggestions for improvement?\n\nPlease take a moment to fill out our feedback survey: [Survey Link]\n\nStay connected with us for future events and updates.\n\nThank you again!\n\nBest regards,\nThe Event Team`
    }
  };

  const handleTemplateSelect = (templateKey) => {
    if (templateKey && templates?.[templateKey]) {
      setMessageData(prev => ({
        ...prev,
        subject: templates?.[templateKey]?.subject,
        message: templates?.[templateKey]?.message
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setMessageData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getRecipients = () => {
    switch (messageData?.sendTo) {
      case 'selected':
        return allAttendees?.filter(a => selectedAttendees?.includes(a?.id)) || [];
      case 'all':
        return allAttendees || [];
      case 'checkedin':
        return allAttendees?.filter(a => a?.checkInStatus === 'checked-in') || [];
      case 'notcheckedin':
        return allAttendees?.filter(a => a?.checkInStatus === 'not-checked-in') || [];
      default:
        return [];
    }
  };

  const getRecipientCount = () => {
    return getRecipients()?.length || 0;
  };

  const handleSendMessage = async () => {
    if (!messageData?.message?.trim()) {
      return;
    }

    setIsLoading(true);

    try {
      const recipients = getRecipients();
      let emailCount = 0;
      let smsCount = 0;
      let errors = [];

      if (messageData?.deliveryMethod === 'email' || messageData?.deliveryMethod === 'both') {
        // Handle email sending (existing functionality)
        emailCount = recipients?.length;
      }

      if (messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both') {
        // Handle SMS sending
        const recipientsWithPhone = recipients?.filter(r => r?.phone);
        
        if (recipientsWithPhone?.length > 0) {
          const smsResult = await SmsService?.sendBulkSMS(recipientsWithPhone, messageData?.message);
          smsCount = smsResult?.totalSent || 0;
          
          if (smsResult?.totalFailed > 0) {
            errors?.push(`${smsResult?.totalFailed} SMS messages failed to send`);
          }
        } else {
          errors?.push('No attendees have phone numbers for SMS delivery');
        }
      }

      // Call the original onSendMessage with enhanced data
      onSendMessage({
        ...messageData,
        recipients: getRecipientCount(),
        emailCount,
        smsCount,
        errors
      });

      // Reset form
      setMessageData({
        subject: '',
        message: '',
        messageType: 'general',
        sendTo: 'selected',
        deliveryMethod: 'email'
      });
      
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      // Handle error appropriately
    }

    setIsLoading(false);
  };

  if (!isOpen) return null;

  const recipients = getRecipients();
  const recipientsWithPhone = recipients?.filter(r => r?.phone)?.length || 0;
  const recipientsWithoutPhone = recipients?.length - recipientsWithPhone;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Mail" size={24} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Send Message to Attendees</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Recipients */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="Users" size={16} className="mr-2" />
              Recipients ({getRecipientCount()} attendees)
            </h3>
            <Select
              options={sendToOptions}
              value={messageData?.sendTo}
              onChange={(value) => handleInputChange('sendTo', value)}
            />
            
            {/* Delivery method info */}
            {messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both' ? (
              <div className="mt-2 text-xs text-muted-foreground">
                📱 {recipientsWithPhone} attendees have phone numbers • {recipientsWithoutPhone} without phone numbers
              </div>
            ) : null}
          </div>

          {/* Delivery Method */}
          <Select
            label="Delivery Method"
            description="Choose how to send your message"
            options={deliveryMethodOptions}
            value={messageData?.deliveryMethod}
            onChange={(value) => handleInputChange('deliveryMethod', value)}
          />

          {/* Message Template */}
          <div>
            <Select
              label="Message Template (Optional)"
              description="Choose a pre-written template to get started quickly"
              options={messageTemplates}
              value=""
              onChange={handleTemplateSelect}
            />
          </div>

          {/* Message Type */}
          <Select
            label="Message Type"
            options={messageTypeOptions}
            value={messageData?.messageType}
            onChange={(value) => handleInputChange('messageType', value)}
          />

          {/* Subject - only for email */}
          {(messageData?.deliveryMethod === 'email' || messageData?.deliveryMethod === 'both') && (
            <Input
              label="Subject"
              type="text"
              placeholder="Enter message subject..."
              value={messageData?.subject}
              onChange={(e) => handleInputChange('subject', e?.target?.value)}
              required
            />
          )}

          {/* Message Body */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Message
            </label>
            <textarea
              className="w-full h-48 px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              placeholder="Enter your message here..."
              value={messageData?.message}
              onChange={(e) => handleInputChange('message', e?.target?.value)}
              maxLength={messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both' ? 1600 : undefined}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use placeholders like [Attendee Name], [Event Name], [Event Date] for personalization
              {(messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both') && 
                ` • SMS: ${messageData?.message?.length || 0}/1600 characters`
              }
            </p>
          </div>

          {/* Message Preview */}
          {messageData?.message && (
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3 flex items-center">
                <Icon name="Eye" size={16} className="mr-2" />
                Message Preview
              </h3>
              <div className="bg-background border border-border rounded-lg p-4 space-y-3">
                {messageData?.subject && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Subject:</span>
                    <p className="text-sm font-medium text-foreground">{messageData?.subject}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-muted-foreground">Message:</span>
                  <div className="text-sm text-foreground whitespace-pre-wrap mt-1">
                    {messageData?.message}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Summary */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="Settings" size={16} className="mr-2" />
              Delivery Summary
            </h3>
            <div className="space-y-2 text-sm">
              {messageData?.deliveryMethod === 'email' || messageData?.deliveryMethod === 'both' ? (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">📧 Email recipients:</span>
                  <span className="font-medium">{getRecipientCount()}</span>
                </div>
              ) : null}
              
              {messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both' ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">📱 SMS recipients:</span>
                    <span className="font-medium">{recipientsWithPhone}</span>
                  </div>
                  {recipientsWithoutPhone > 0 && (
                    <div className="flex items-center justify-between text-yellow-600">
                      <span>⚠️ No phone number:</span>
                      <span className="font-medium">{recipientsWithoutPhone}</span>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {messageData?.deliveryMethod === 'sms' || messageData?.deliveryMethod === 'both' ?'Standard SMS rates may apply' 
              : `This message will be sent to ${getRecipientCount()} attendees`
            }
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendMessage}
              disabled={isLoading || !messageData?.message?.trim()}
              iconName="Send"
              iconPosition="left"
            >
              {isLoading ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageModal;